import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolOptions, getTraderSMCardHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, searchSymbols } from '../../helpers';
import { InsiderTradingVolumeConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface InsiderTradingVolumeChartItem {
    label: string;
    symbol: string;
    total_value: number;
    total_securities: number;
    acquisitions: number;
    transaction_count: number;
}

interface InsiderTradingVolumeApiData {
    parameters: {
        symbol: string | null;
        days_back: number;
        min_transaction_value: number;
        show_top_n: number;
        n_largest_trades: number;
        cutoff_date: string;
        query_date: string;
    };
    overall_statistics: Record<string, unknown>;
    top_symbols_summary: Array<{
        symbol: string;
        total_value: number;
        total_securities: number;
        acquisitions: number;
        transaction_count: number;
    }>;
    chart_data: {
        symbols?: string[];
        insiders?: string[];
        names?: string[];
        total_values: number[];
        total_securities: number[];
        acquisitions: number[];
        transaction_counts: number[];
    };
    largest_trades: {
        count: number;
        data: unknown[];
    };
    detailed_transactions: unknown[];
}

export class TraderverseTopNSymbolsByInsiderTradingVolume {
    private static readonly ROW_HEIGHT = 34;
    private static readonly BAR_HEIGHT = 16;
    private static readonly GRID_PADDING = 52;

    private container: HTMLElement;
    private config: InsiderTradingVolumeConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private chartData: InsiderTradingVolumeChartItem[] = [];
    private apiData: InsiderTradingVolumeApiData | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private selectedSymbols: string[] = [];
    private filters = {
        daysBack: 90 as number | '',
        minTransactionValue: 500000 as number | '',
        showTopN: 10 as number | '',
        nLargestTrades: 5 as number | ''
    };

    constructor(config: InsiderTradingVolumeConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<InsiderTradingVolumeConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            daysBack: 90,
            minTransactionValue: 500000,
            showTopN: 10,
            nLargestTrades: 5
        };
    }

    private applyConfigFilters(): void {
        if (this.config.symbol) {
            this.selectedSymbols = this.config.symbol.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (typeof this.config.daysBack === 'number') this.filters.daysBack = this.config.daysBack;
        if (typeof this.config.minTransactionValue === 'number') this.filters.minTransactionValue = this.config.minTransactionValue;
        if (typeof this.config.showTopN === 'number') this.filters.showTopN = this.config.showTopN;
        if (typeof this.config.nLargestTrades === 'number') this.filters.nLargestTrades = this.config.nLargestTrades;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Top N Symbols By Insider Trading Volume</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              ${this.config.hideSymbol ? '' : `<div class="item dropStyle02">
                <span id="symbol-dropdown-placeholder"></span>
                <div id="selected-symbols" class="symbolsCol"></div>
              </div>`}
              <span></span>
              <div class="filtersBox sideBar hidden">
                <div class="item">
                  <label>Days Back</label>
                  <input type="number" class="filter-days-back" min="1" step="1" placeholder="e.g. 90" value="${this.filters.daysBack}" />
                </div>
                <div class="item">
                  <label>Min Transaction Value</label>
                  <input type="number" class="filter-min-transaction-value" min="0" step="1" placeholder="e.g. 500000" value="${this.filters.minTransactionValue}" />
                </div>
                <div class="item">
                  <label>Show Top N</label>
                  <input type="number" class="filter-show-top-n" min="1" step="1" placeholder="e.g. 10" value="${this.filters.showTopN}" />
                </div>
                <div class="item">
                  <label>N Largest Trades</label>
                  <input type="number" class="filter-n-largest-trades" min="1" step="1" placeholder="e.g. 5" value="${this.filters.nLargestTrades}" />
                </div>
              </div>
            </div>
            <div class="chartDv" id="insiderTradingVolume">
                <div class="insiderVolumeScale">
                    <div class="insiderVolumeScaleBar"></div>
                    <div class="insiderVolumeScaleTicks" id="insiderVolumeScaleTicks"></div>
                </div>
                <div id="chartContainer" class="chart insiderVolumeChart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.renderSelectedSymbols();
        this.setupMultiSymbolDropdown();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        setupHeaderPin(this.shadowRoot, this.config);

        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filtersToggle && filtersBox) {
            filtersToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }

        const updateNumericFiltersAndFetch = () => {
            this.filters.daysBack = this.readNumberInput('.filter-days-back');
            this.filters.minTransactionValue = this.readNumberInput('.filter-min-transaction-value');
            this.filters.showTopN = this.readNumberInput('.filter-show-top-n');
            this.filters.nLargestTrades = this.readNumberInput('.filter-n-largest-trades');
            this.setupChart();
        };

        [
            '.filter-days-back',
            '.filter-min-transaction-value',
            '.filter-show-top-n',
            '.filter-n-largest-trades'
        ].forEach((selector) => {
            const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
            input?.addEventListener('change', updateNumericFiltersAndFetch);
        });
    }

    private readNumberInput(selector: string): number | '' {
        const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
        if (!input || input.value.trim() === '') return '';
        const value = parseFloat(input.value);
        return Number.isNaN(value) ? '' : value;
    }

    private setupMultiSymbolDropdown(): void {
        if (!this.config.isSymbolSelectable || this.config.hideSymbol) return;

        const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
        if (!placeholder) return;

        const dropdownContainerOutter = document.createElement('div');
        dropdownContainerOutter.className = 'itemInner';
        const dropdownContainer = document.createElement('div');
        dropdownContainer.className = 'symbol-dropdown-container';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'symbol-search-input';
        input.placeholder = 'Search symbol...';

        const dropdownList = document.createElement('div');
        dropdownList.className = 'symbol-dropdown-list-multiple';

        const getAllOptions = () => ((window as any).getSymbolOptions ? (window as any).getSymbolOptions() : getSymbolOptions());

        let timeout = null as any;
        let requestId = 0;
        const renderDropdown = (filter = '') => {
            clearTimeout(timeout);
            timeout = setTimeout(async () => {
            const id = ++requestId;
            dropdownList.innerHTML = '<div style="padding:8px 12px;color:#888;">Searching...</div>';
            const keyword = filter.trim();
            let options = await searchSymbols({ keyword, market: 'stock' });
            if (id !== requestId) return;
            if (!keyword) {
                const selectedMissing = this.selectedSymbols
                    .filter((s) => !options.some((item: any) => item.symbol === s))
                    .map((s) => getAllOptions().find((item: any) => item.symbol === s) || { symbol: s, name: s, type: 'stock' });
                options = [...selectedMissing, ...options];
            }
            const selected = options.filter((opt: any) => this.selectedSymbols.includes(opt.symbol));
            const unselected = options.filter((opt: any) => !this.selectedSymbols.includes(opt.symbol));
            const sorted = [...selected, ...unselected];
            dropdownList.innerHTML = '';
            if (sorted.length === 0) {
                dropdownList.innerHTML = '<div style="padding:8px 12px;color:#888;">No results</div>';
                return;
            }
            sorted.forEach(opt => {
                const divOutter = document.createElement('div');
                divOutter.className = 'ditem';
                const div = document.createElement('div');
                div.className = 'trader-sm-card';
                if (this.selectedSymbols.includes(opt.symbol)) {
                    divOutter.classList.add('selected-symbol-item');
                }
                div.innerHTML = getTraderSMCardHTML(opt);
                divOutter.innerHTML = '<div class="checkIcon"><i><svg width="7" height="5" viewBox="0 0 7 5" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.5 2.5L2.42929 4.42929C2.46834 4.46834 2.53166 4.46834 2.57071 4.42929L6.5 0.5" stroke="white" stroke-linecap="round"/> </svg> </i></div><div class="trader-sm-card">' + div.innerHTML + '</div>';
                divOutter.onclick = (e) => {
                    e.stopPropagation();
                    if (this.selectedSymbols.includes(opt.symbol)) {
                        this.selectedSymbols = this.selectedSymbols.filter(s => s !== opt.symbol);
                    } else {
                        this.selectedSymbols.push(opt.symbol);
                    }
                    this.config.symbol = this.selectedSymbols.join(',');
                    renderDropdown(input.value);
                    this.renderSelectedSymbols();
                    this.setupChart();
                };
                dropdownList.appendChild(divOutter);
            });
            }, 300);
        };

        input.addEventListener('focus', () => {
            dropdownList.style.display = 'block';
            renderDropdown(input.value);
        });
        input.addEventListener('input', () => {
            dropdownList.style.display = 'block';
            renderDropdown(input.value);
        });
        input.addEventListener('blur', () => {
            setTimeout(() => dropdownList.style.display = 'none', 150);
        });

        dropdownContainer.appendChild(input);
        dropdownContainer.appendChild(dropdownList);
        dropdownContainerOutter.appendChild(dropdownContainer);
        placeholder.replaceWith(dropdownContainerOutter);

        renderDropdown();
    }

    private renderSelectedSymbols(): void {
        const container = this.shadowRoot.getElementById('selected-symbols');
        if (!container) return;

        const symbolOptions = ((window as any).getSymbolOptions ? (window as any).getSymbolOptions() : getSymbolOptions());
        container.innerHTML = this.selectedSymbols.map(symbol => {
            const symInfo = symbolOptions.find((s: any) => s.symbol === symbol) || { symbol };
            const logoImg = symInfo.type === 'crypto'
                ? `<img src="https://media.traderverse.io/assets/crypto/${symbol.toUpperCase()}_CRYPTO.svg" alt="${symbol}" onerror="this.onerror=null;this.src='https://cdn-images.traderverse.io/crypto_dummy.svg';">`
                : `<img src="https://imagedelivery.net/09I_Y_JIwzvjUiGrV3dv3Q/stocks_v1/${symbol}.svg/public" alt="${symbol}">`;
            return `<figure>${logoImg}</figure>`;
        }).join('') + '<svg width="11" height="6" viewBox="0 0 11 6" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.706223 0.706055L5.41424 4.47247L10.1223 0.706055" stroke="#9A95AC" stroke-width="1.41241" stroke-linecap="round" stroke-linejoin="round"/> </svg>';
    }

    private buildApiUrl(): string {
        const params: Record<string, string> = {};
        if (this.filters.daysBack !== '') params.daysBack = String(this.filters.daysBack);
        if (this.filters.minTransactionValue !== '') params.minTransactionValue = String(this.filters.minTransactionValue);
        if (this.filters.showTopN !== '') params.showTopN = String(this.filters.showTopN);
        if (this.filters.nLargestTrades !== '') params.nLargestTrades = String(this.filters.nLargestTrades);

        const qsParts = Object.keys(params)
            .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`);

        if (this.selectedSymbols.length > 0) {
            qsParts.push(`symbols=${encodeURIComponent(JSON.stringify(this.selectedSymbols))}`);
        }

        const qs = qsParts.join('&');
        return `${APIURL}/v1/equity-market/insider-trading-volume${qs ? `?${qs}` : ''}`;
    }

    private mapChartData(data: InsiderTradingVolumeApiData | null | undefined): InsiderTradingVolumeChartItem[] {
        const chartData = data?.chart_data;
        if (chartData?.symbols?.length && chartData.total_values?.length) {
            return chartData.symbols.map((symbol, index) => ({
                label: symbol,
                symbol,
                total_value: chartData.total_values[index] ?? 0,
                total_securities: chartData.total_securities?.[index] ?? 0,
                acquisitions: chartData.acquisitions?.[index] ?? 0,
                transaction_count: chartData.transaction_counts?.[index] ?? 0
            }));
        }

        const summary = data?.top_symbols_summary ?? [];
        if (!summary.length) return [];

        return summary.map((row) => ({
            label: row.symbol,
            symbol: row.symbol,
            total_value: row.total_value ?? 0,
            total_securities: row.total_securities ?? 0,
            acquisitions: row.acquisitions ?? 0,
            transaction_count: row.transaction_count ?? 0
        }));
    }

    private parseApiPayload(json: unknown): InsiderTradingVolumeApiData | null {
        if (!json || typeof json !== 'object') return null;
        const envelope = json as { isSuccess?: boolean; data?: InsiderTradingVolumeApiData };
        if (envelope.isSuccess === false) return null;
        return (envelope.data ?? json) as InsiderTradingVolumeApiData;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
            this.injectScaleLegendStyles();
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#insiderTradingVolume') as HTMLElement;
        if (widgetBox) widgetBox.classList.add('loading');

        try {
            const res = await apiGet({
                method: 'GET',
                url: this.buildApiUrl(),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            this.apiData = this.parseApiPayload(json);
            this.chartData = this.mapChartData(this.apiData);

            if (this.chartData.length) {
                containerWrapper?.setAttribute('data-testing-id', 'insider-trading-volume');
            } else {
                containerWrapper?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.chartData = [];
            this.apiData = null;
            containerWrapper?.removeAttribute('data-testing-id');
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widgetBox) widgetBox.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private formatCurrency(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        if (abs >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
        return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }

    private formatBarCurrency(value: number): string {
        const formatted = Math.abs(value).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return `US$ ${formatted}`;
    }

    private formatCompactCurrency(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1e12) return `${Math.round(value / 1e12)}T`;
        if (abs >= 1e9) return `${Math.round(value / 1e9)}B`;
        if (abs >= 1e6) return `${Math.round(value / 1e6)}M`;
        if (abs >= 1e3) return `${Math.round(value / 1e3)}K`;
        return `${Math.round(value)}`;
    }

    private formatAxisCurrency(value: number): string {
        if (value === 0) return '$0';
        return `$${this.formatCompactCurrency(value)}`;
    }

    private getNiceAxisMax(maxValue: number): number {
        if (maxValue <= 0) return 1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
        const normalized = maxValue / magnitude;
        const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
        return niceNormalized * magnitude;
    }

    private getGradientColors(): string[] {
        return ['#57307A', '#6E2B62', '#8F2E45', '#B8432A', '#D9791A', '#E8A820', '#F5D020', '#FFEC00'];
    }

    private renderVolumeScaleLegend(maxValue: number): void {
        const ticksEl = this.shadowRoot.querySelector('#insiderVolumeScaleTicks') as HTMLElement | null;
        if (!ticksEl) return;

        const tickCount = 5;
        const labels = Array.from({ length: tickCount + 1 }, (_, index) => {
            const value = (maxValue / tickCount) * index;
            return value === 0 ? '0' : this.formatCompactCurrency(value);
        });

        ticksEl.innerHTML = labels.map((label) => `<span>${label}</span>`).join('');
    }

    private getNeutralLabelColor(textColor: string): string {
        const trimmed = textColor.trim();
        return trimmed || '#B8B3C9';
    }

    private getChartHeight(barCount: number): number {
        return Math.max(
            220,
            barCount * TraderverseTopNSymbolsByInsiderTradingVolume.ROW_HEIGHT
                + TraderverseTopNSymbolsByInsiderTradingVolume.GRID_PADDING
        );
    }

    private formatBarLabelCurrency(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1e12) return `US$ ${(value / 1e12).toFixed(2)}T`;
        if (abs >= 1e9) return `US$ ${(value / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `US$ ${(value / 1e6).toFixed(2)}M`;
        if (abs >= 1e3) return `US$ ${(value / 1e3).toFixed(2)}K`;
        return this.formatBarCurrency(value);
    }

    private updateWidgetLayout(chartHeight: number): void {
        const scaleLegendHeight = 48;
        const chartDv = this.shadowRoot.querySelector('#insiderTradingVolume') as HTMLElement | null;
        const inBody = this.shadowRoot.querySelector('.inBody') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        const chartDvHeight = scaleLegendHeight + chartHeight;
        if (chartDv) {
            chartDv.style.height = `${chartDvHeight}px`;
            chartDv.style.minHeight = `${chartDvHeight}px`;
            chartDv.style.flex = '0 0 auto';
        }

        if (this.config.autosize === false) return;

        const optionsRow = this.shadowRoot.querySelector('.optionsRow') as HTMLElement | null;
        const headTop = this.shadowRoot.querySelector('.headTop') as HTMLElement | null;
        const optionsRowHeight = optionsRow?.offsetHeight ?? 50;
        const headTopHeight = headTop?.offsetHeight ?? 56;
        const inBodyPadding = 30;
        const inBodyHeight = optionsRowHeight + chartDvHeight + inBodyPadding;

        if (inBody) {
            inBody.style.height = `${inBodyHeight}px`;
            inBody.style.minHeight = `${inBodyHeight}px`;
        }
        if (widgetBox) {
            widgetBox.style.height = `${headTopHeight + inBodyHeight}px`;
        }
    }

    private injectScaleLegendStyles(): void {
        const existing = this.shadowRoot.querySelector('style[data-insider-volume-scale]');
        if (existing) existing.remove();

        const style = document.createElement('style');
        style.setAttribute('data-insider-volume-scale', 'true');
        style.textContent = `
            #insiderTradingVolume.chartDv {
                height: auto;
                min-height: 300px;
                display: flex;
                flex-direction: column;
                flex: 0 0 auto;
                overflow: visible;
            }
            .insiderVolumeScale {
                width: 100%;
                padding: 4px 0 12px;
                box-sizing: border-box;
                flex: 0 0 auto;
            }
            .insiderVolumeScaleBar {
                width: 33.333%;
                height: 18px;
                border-radius: 9px;
                background: linear-gradient(90deg, #57307A 0%, #6E2B62 14%, #8F2E45 28%, #B8432A 42%, #D9791A 57%, #E8A820 71%, #F5D020 85%, #FFEC00 100%);
            }
            .insiderVolumeScaleTicks {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 33.333%;
                margin-top: 10px;
                color: var(--charts-text-color, #9A95AC);
                font-size: var(--font-size-sm, 12px);
                line-height: 1;
            }
            .insiderVolumeScaleTicks span:first-child {
                text-align: left;
            }
            .insiderVolumeScaleTicks span:last-child {
                text-align: right;
            }
            .insiderVolumeScaleTicks span {
                flex: 1;
                text-align: center;
            }
            .insiderVolumeScaleTicks span:first-child,
            .insiderVolumeScaleTicks span:last-child {
                flex: 0 0 auto;
            }
            #insiderTradingVolume .insiderVolumeChart {
                position: relative !important;
                left: auto !important;
                top: auto !important;
                width: 100%;
                flex: 0 0 auto;
                min-height: 200px;
            }
            .widgetBox:has(#insiderTradingVolume) .inBody {
                height: auto;
                min-height: 420px;
                overflow: visible;
            }
        `;
        this.shadowRoot.appendChild(style);
    }

    private drawChart(): void {
        const wrapper = this.shadowRoot.querySelector('#chartContainer') as HTMLElement;
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#insiderTradingVolume') as HTMLElement;

        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const chartData = [...this.chartData].reverse();
        if (!chartData.length) {
            container.innerHTML = getNoDataHTML();
            const ticksEl = this.shadowRoot.querySelector('#insiderVolumeScaleTicks') as HTMLElement | null;
            if (ticksEl) ticksEl.innerHTML = '';
            containerWrapper?.removeAttribute('data-testing-id');
            return;
        }

        container.innerHTML = '';
        containerWrapper?.setAttribute('data-testing-id', 'insider-trading-volume');

        const yAxisData = chartData.map((item) => item.label);
        const volumeValues = chartData.map((item) => item.total_value);
        const maxValue = this.getNiceAxisMax(Math.max(...volumeValues, 0));
        this.renderVolumeScaleLegend(maxValue);

        const barCount = chartData.length;
        const chartHeight = this.getChartHeight(barCount);
        container.style.height = `${chartHeight}px`;
        container.style.minHeight = `${chartHeight}px`;
        container.style.width = '100%';
        this.updateWidgetLayout(chartHeight);

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const widgetStyles = getComputedStyle(widgetBox);
        const textColor = widgetStyles.getPropertyValue('--charts-text-color');
        const axisLineColor = widgetStyles.getPropertyValue('--charts-axis-color');
        const splitLineColor = widgetStyles.getPropertyValue('--charts-split-line-color');
        const fontSize = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const fontSizeMd = widgetStyles.getPropertyValue('--font-size-md').trim() || '14px';
        const labelFontSize = parseInt(fontSize, 10) || 12;
        const neutralLabelColor = this.getNeutralLabelColor(textColor);
        const rowHeight = TraderverseTopNSymbolsByInsiderTradingVolume.ROW_HEIGHT;

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow', label: { show: false } },
                confine: true,
                appendToBody: false,
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const item = params[0];
                    const index = item.dataIndex;
                    const row = chartData[index];

                    return `
                    <div style="font-size:${fontSize}; line-height:1.45; max-width:280px;">
                        <div style="font-size:${fontSizeMd}; font-weight:600; margin-bottom:4px;">${row.symbol}</div>
                        <hr style="margin:6px 0; border:0; border-top:1px solid ${axisLineColor};">
                        <div>Total Value: <b>${this.formatBarCurrency(row.total_value)}</b></div>
                        <div>Total Securities: <b>${row.total_securities.toLocaleString()}</b></div>
                        <div>Acquisitions: <b>${row.acquisitions}</b></div>
                        <div>Transactions: <b>${row.transaction_count}</b></div>
                    </div>
                `;
                }
            },
            visualMap: {
                show: false,
                type: 'continuous',
                min: 0,
                max: maxValue,
                dimension: 0,
                calculable: false,
                inRange: {
                    color: this.getGradientColors()
                }
            },
            grid: { left: 8, right: 88, top: 8, bottom: 36, containLabel: true },
            xAxis: {
                type: 'value',
                min: 0,
                max: maxValue,
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    margin: 10,
                    formatter: (value: number) => this.formatAxisCurrency(value)
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dashed'
                    }
                },
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                }
            },
            yAxis: {
                type: 'category',
                data: yAxisData,
                boundaryGap: true,
                axisLabel: {
                    color: neutralLabelColor,
                    fontSize: labelFontSize,
                    interval: 0,
                    margin: 12,
                    width: 170,
                    overflow: 'truncate',
                    align: 'right',
                    verticalAlign: 'middle',
                    lineHeight: rowHeight,
                    height: rowHeight
                },
                axisTick: {
                    show: false,
                    alignWithLabel: true
                },
                axisLine: {
                    show: false
                },
                splitLine: {
                    show: false
                }
            },
            series: [
                {
                    name: 'Trading Volume',
                    type: 'bar',
                    data: volumeValues,
                    barWidth: TraderverseTopNSymbolsByInsiderTradingVolume.BAR_HEIGHT,
                    barMaxWidth: TraderverseTopNSymbolsByInsiderTradingVolume.BAR_HEIGHT,
                    barCategoryGap: 2,
                    itemStyle: {
                        borderRadius: [0, 5, 5, 0]
                    },
                    label: {
                        show: true,
                        position: 'right',
                        distance: 10,
                        verticalAlign: 'middle',
                        align: 'left',
                        formatter: (params: any) => this.formatBarLabelCurrency(Number(params.value)),
                        color: neutralLabelColor,
                        fontSize: labelFontSize,
                        fontWeight: 500
                    },
                    labelLayout: {
                        hideOverlap: true
                    },
                    emphasis: {
                        focus: 'none',
                        scale: false,
                        itemStyle: {
                            opacity: 1
                        },
                        label: {
                            color: neutralLabelColor
                        }
                    },
                    blur: {
                        label: {
                            color: neutralLabelColor
                        }
                    },
                    select: {
                        disabled: true
                    }
                }
            ],
            animation: true,
            animationDuration: 1000,
            animationEasing: 'cubicOut'
        };

        const chart = echarts.init(container, null, { renderer: 'canvas' });
        chart.setOption(option, true);
        chart.resize({ width: container.clientWidth || undefined, height: chartHeight });

        requestAnimationFrame(() => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
        });

        const resizeHandler = () => {
            const nextHeight = this.getChartHeight(barCount);
            container.style.height = `${nextHeight}px`;
            container.style.minHeight = `${nextHeight}px`;
            chart.resize({ width: container.clientWidth || undefined, height: nextHeight });
            this.updateWidgetLayout(nextHeight);
        };
        window.addEventListener('resize', resizeHandler);
        chart.on('disposed', () => {
            window.removeEventListener('resize', resizeHandler);
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        if (!this.selectedSymbols.includes(stockId)) {
            this.selectedSymbols.push(stockId);
            this.config.symbol = this.selectedSymbols.join(',');
            this.renderSelectedSymbols();
            this.setupChart();
        }
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, () => this.setupChart(), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }
    public destroy(): void {
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseTopNSymbolsByInsiderTradingVolume: typeof TraderverseTopNSymbolsByInsiderTradingVolume; } }
if (typeof window !== 'undefined') { window.TraderverseTopNSymbolsByInsiderTradingVolume = TraderverseTopNSymbolsByInsiderTradingVolume; }
